import type { InferRequestType } from "hono/client";
import { defaultsDeep } from "lodash";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { RenderSite } from "../../../../../server/src/render/RenderSite";
import { useApi, useTranslations } from "../../../contexts";
import { client } from "../../../helpers";
import { routerPaths } from "../../routerPaths";
import { SidebarForm } from "./SidebarForm";
import { Site } from "../../../types";
import { ImageUploadMetadata } from "../../../components/ImageUpload";
import {
  CardSkeleton,
  LoadingErrorState,
  PageSkeleton,
  TextSkeleton,
} from "../../../components";
import { withSiteId } from "../../../helpers/imageUri";

export type ClientSitePatch = (typeof client.api)["sites"][":siteId"]["$get"];
export type ClientSitePatchBody = InferRequestType<
  (typeof client.api)["sites"][":siteId"]["$patch"]
>["json"];

const previewSectionIds = [
  "hero",
  "features",
  "gallery",
  "about",
  "text",
  "faq",
  "contact",
] as const;

type PreviewSectionId = (typeof previewSectionIds)[number];

const isPreviewSectionId = (value?: string): value is PreviewSectionId =>
  previewSectionIds.some((sectionId) => sectionId === value);

const getPreviewSectionId = (
  target: EventTarget | null
): PreviewSectionId | null => {
  if (!(target instanceof Element)) return null;
  const sectionId = target.closest<HTMLElement>("[data-preview-section]")
    ?.dataset.previewSection;
  return isPreviewSectionId(sectionId) ? sectionId : null;
};

export const SiteEditPage: React.FC = () => {
  const { siteId } = useParams<Parameters<typeof routerPaths.siteEdit>["0"]>();
  if (!siteId) {
    throw new Error(`siteId is undefined`);
  }
  const [isDirty, setIsDirty] = useState(false);
  const [form, _setForm] = useState<ClientSitePatchBody>();
  const [imageMetadata, setImageMetadata] = useState<Record<string, ImageUploadMetadata>>({});
  const previewRef = useRef<HTMLDivElement>(null);
  const pendingPreviewSectionRef = useRef<PreviewSectionId | null>(null);
  const scrollFrameRef = useRef<number>();
  const setForm: Dispatch<SetStateAction<ClientSitePatchBody | undefined>> = (
    state
  ) => {
    setIsDirty(true);
    _setForm(state);
  };

  const api = useApi();
  const i18n = useTranslations();
  const siteQuery = api.siteById({ siteId });
  const site = siteQuery?.data;

  useEffect(() => {
    if (!site) return;
    const siteWithDefaults = defaultsDeep(
      {
        hero: {
          title: site?.hero?.title || site?.name,
          cta: i18n["Contact us"],
          cta_link: "#contact",
        },
        services: {
          title: site?.services?.title || i18n["Our services"],
        },
        contactForm: {
          title:
            site?.contactForm?.title ||
            i18n["Still have questions? Get in touch."],
        },
        faq: {
          title:
            site?.faq?.title || i18n["Frequently asked questions"],
        },
      } as Partial<Site>,
      site
    );
    _setForm(siteWithDefaults);
  }, [site, i18n]);

  useEffect(
    () => () => {
      if (scrollFrameRef.current !== undefined) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    []
  );

  const scrollToPreviewSection = useCallback((sectionId: PreviewSectionId) => {
    pendingPreviewSectionRef.current = sectionId;
    if (scrollFrameRef.current !== undefined) {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }

    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = undefined;
      const preview = previewRef.current;
      const section = preview?.querySelector<HTMLElement>(`#${sectionId}`);
      if (!preview || !section) return;
      if (pendingPreviewSectionRef.current === sectionId) {
        pendingPreviewSectionRef.current = null;
      }

      const previewRect = preview.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      const scrollPadding = 16;
      const availableHeight = Math.max(
        0,
        preview.clientHeight - scrollPadding * 2
      );
      const sectionTop =
        preview.scrollTop + sectionRect.top - previewRect.top;
      const requestedScrollTop =
        sectionRect.height > availableHeight
          ? sectionTop - scrollPadding
          : sectionTop - (preview.clientHeight - sectionRect.height) / 2;
      const maxScrollTop = Math.max(
        0,
        preview.scrollHeight - preview.clientHeight
      );
      const top = Math.min(
        maxScrollTop,
        Math.max(0, requestedScrollTop)
      );
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      preview.scrollTo({
        top,
        behavior: prefersReducedMotion ? "auto" : "smooth",
      });
    });
  }, []);

  useEffect(() => {
    const pendingSectionId = pendingPreviewSectionRef.current;
    if (pendingSectionId) {
      scrollToPreviewSection(pendingSectionId);
    }
  }, [form, scrollToPreviewSection]);

  const activatePreviewSection = (target: EventTarget | null) => {
    const sectionId = getPreviewSectionId(target);
    if (!sectionId) return;
    scrollToPreviewSection(sectionId);
  };

  if (!siteQuery || siteQuery.isPending || siteQuery.isLoading) {
    return (
      <div className="flex h-full overflow-hidden p-4">
        <div className="h-full w-[500px] border-e p-4">
          <TextSkeleton className="h-10 w-full" />
          <TextSkeleton className="mt-6 h-8 w-1/3" />
          <CardSkeleton className="mt-4" />
          <CardSkeleton className="mt-4" />
        </div>
        <div className="grow p-6">
          <PageSkeleton
            className="max-w-none"
            cards={2}
            titleWidthClassName="w-64"
            ariaLabel={i18n["Loading page content"]}
            loadingLabel={i18n["Loading..."]}
          />
        </div>
      </div>
    );
  }
  if (siteQuery.isError) {
    return (
      <LoadingErrorState
        className="m-4"
        message={i18n["Failed to load the editor."]}
        retryLabel={i18n["Retry"]}
        onRetry={() => siteQuery.refetch()}
      />
    );
  }

  if (!site) {
    return (
      <LoadingErrorState
        className="m-4"
        message={i18n["Could not find this site."]}
        retryLabel={i18n["Retry"]}
        onRetry={() => siteQuery.refetch()}
      />
    );
  }

  if (!form) {
    return (
      <PageSkeleton
        className="p-4"
        cards={3}
        ariaLabel={i18n["Loading page content"]}
        loadingLabel={i18n["Loading..."]}
      />
    );
  }

  return (
    <div className="flex h-full overflow-hidden" aria-busy={siteQuery.isFetching}>
      <div
        className="h-full w-[500px] shrink-0 border-e overflow-y-scroll"
        onClickCapture={(event) => activatePreviewSection(event.target)}
        onFocusCapture={(event) => activatePreviewSection(event.target)}
      >
        <SidebarForm
          isDirty={isDirty}
          setIsDirty={setIsDirty}
          form={form}
          setForm={setForm}
          siteId={siteId}
          imageMetadata={imageMetadata}
          setImageMetadata={setImageMetadata}
        />
      </div>
      <div
        ref={previewRef}
        className="h-full min-w-0 flex-1 overflow-y-scroll"
      >
        <RenderSite
          site={{
            ...site,
            ...form,
            hero: {
              ...site.hero,
              ...form.hero,
              image_uri:
                form.hero?.image_uri && withSiteId(form.hero?.image_uri, site.id),
            },
            services: {
              ...site.services,
              ...form.services,
              items: form.services?.items,
            },
            photos: {
              ...site.photos,
              ...form.photos,
              items:
                form.photos?.items?.map(({ uri, ...item }) => ({
                  created_at: "",
                  updated_at: "",
                  is_enabled: 1,
                  photo_id: "",
                  id: "",
                  alt: "wip",
                  ...item,
                  uri: withSiteId(uri, site.id) || "",
                })) || [],
            },
            teams: {
              ...site.teams,
              ...form.teams,
              items:
                form.teams?.items?.map((member) => ({
                  ...member,
                  photo_uri: member.photo_uri
                    ? withSiteId(member.photo_uri, site.id)
                    : member.photo_uri,
                })) || [],
            },
            text: {
              ...site.text,
              ...form.text,
            },
            faq: {
              ...site.faq,
              ...form.faq,
              items: form.faq?.items,
            },
          }}
        />
      </div>
    </div>
  );
};
